import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { estimatesAPI, customersAPI, usersAPI, invoicesAPI, leadSourcesAPI, tagsAPI, maintenancePlansAPI, priceBookAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AsyncSearchableSelect } from '../components/ui/async-searchable-select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import {
  ArrowLeft, Loader2, Plus, Trash2, Save, Edit2, X, FileText, User, Phone,
  MapPin, Clock, Tag, DollarSign, Briefcase, MessageSquare, Receipt,
  CheckCircle2, ArrowRight, Layers, ChevronDown, ChevronRight, Check,
  Link2, Copy, ExternalLink, XCircle, Shield, Mail, Send, BookOpen, Star, ImageIcon, Video, Upload, Download, CreditCard, RefreshCw, MoreVertical, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { PriceBookPicker } from '../components/PriceBookPicker';
import { ServiceTemplatePicker } from '../components/ServiceTemplatePicker';
import { LocationPicker, LocationBadge } from '../components/LocationPicker';
import { BillingBadge } from '../components/BillingBadge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  { value: 'unscheduled', label: 'Unscheduled', color: 'bg-gray-100 text-gray-700' },
  { value: 'scheduled', label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  { value: 'sent', label: 'Sent', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'presented', label: 'Presented', color: 'bg-violet-100 text-violet-700' },
  { value: 'awaiting_approval', label: 'Awaiting Approval', color: 'bg-purple-100 text-purple-700' },
  { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-700' },
  { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-700' },
  { value: 'expired', label: 'Expired', color: 'bg-orange-100 text-orange-700' },
  { value: 'completed', label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'copied_to_job', label: 'Converted to Job', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'converted_to_job', label: 'Converted to Job', color: 'bg-indigo-100 text-indigo-700' },
];

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  return <Badge className={`${opt.color} text-xs`}>{opt.label}</Badge>;
}

export default function EstimateEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;
  const isNew = id === 'new';

  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [users, setUsers] = useState([]);

  // Lead sources + tags
  const [leadSources, setLeadSources] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);

  // Local state for fields that need debounced save (prevent keystroke-level API calls)
  const [localName, setLocalName] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  // Item editing
  const [newItem, setNewItem] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemForm, setItemForm] = useState({});
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [pbPickerOpen, setPbPickerOpen] = useState(false);
  const [pbPickerOptionId, setPbPickerOptionId] = useState(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  // Options
  const [optionName, setOptionName] = useState('');
  const [creatingOption, setCreatingOption] = useState(false);
  const [expandedOptions, setExpandedOptions] = useState({});

  // Upsells
  const [newUpsell, setNewUpsell] = useState(null);
  const [editingUpsellId, setEditingUpsellId] = useState(null);
  const [upsellForm, setUpsellForm] = useState({});
  const [maintenancePlans, setMaintenancePlans] = useState([]);

  // Auto-create for /estimates/new
  useEffect(() => {
    if (!isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await estimatesAPI.create({});
        if (cancelled) return;
        // Replace URL so back button goes to /estimates, not /estimates/new
        navigate(`/estimates/${res.data.id}`, { replace: true });
      } catch {
        toast.error('Failed to create estimate');
        navigate('/estimates');
      }
    })();
    return () => { cancelled = true; };
  }, [isNew, navigate]);

  const fetchEstimate = useCallback(async () => {
    if (isNew) return; // Will be handled by auto-create
    try {
      const res = await estimatesAPI.get(id);
      setEstimate(res.data);
      // Sync local name state (only if not currently being edited)
      if (!nameDirty) setLocalName(res.data.estimate_name || '');
      // Auto-expand all options
      const exp = {};
      (res.data.options || []).forEach(o => { exp[o.id] = true; });
      setExpandedOptions(prev => {
        const merged = { ...prev };
        (res.data.options || []).forEach(o => {
          if (!(o.id in merged)) merged[o.id] = true;
        });
        return merged;
      });
      if (res.data.customer_id) {
        try {
          const cres = await customersAPI.get(res.data.customer_id);
          setCustomer(cres.data);
        } catch { setCustomer(null); }
      }
    } catch {
      toast.error('Failed to load estimate');
      navigate('/estimates');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchEstimate(); }, [fetchEstimate]);

  // Load maintenance plans for upsell linking
  useEffect(() => {
    maintenancePlansAPI.list(true).then(r => setMaintenancePlans(r.data.plans || [])).catch(() => {});
  }, []);

  useEffect(() => {
    usersAPI.list({ include_archived: false }).then(res => {
      const u = res.data?.users || res.data || [];
      setUsers(Array.isArray(u) ? u : []);
    }).catch(() => {});
    // Fetch lead sources
    leadSourcesAPI.list().then(res => {
      setLeadSources(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {});
    // Fetch existing estimate tags for suggestions
    tagsAPI.list({ entity_type: 'estimate' }).then(res => {
      setTagSuggestions(Array.isArray(res.data) ? res.data.map(t => t.name) : []);
    }).catch(() => {
      // Tags might not have 'estimate' entity_type yet, gather from existing estimates
      estimatesAPI.list({ page_size: 100 }).then(res => {
        const allTags = new Set();
        (res.data.estimates || []).forEach(e => {
          if (e.tags) e.tags.split(',').forEach(t => { const tr = t.trim(); if (tr) allTags.add(tr); });
        });
        // Also check current estimate tags from response
        setTagSuggestions([...allTags]);
      }).catch(() => {});
    });
  }, []);

  // Update estimate fields
  const updateField = async (field, value) => {
    setSaving(true);
    try {
      await estimatesAPI.update(id, { [field]: value });
      await fetchEstimate();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  // Explicit save for locally-managed fields
  const hasUnsavedChanges = nameDirty;
  const saveEstimate = async () => {
    if (!hasUnsavedChanges) return;
    setSaving(true);
    try {
      const updates = {};
      if (nameDirty) updates.estimate_name = localName;
      await estimatesAPI.update(id, updates);
      setNameDirty(false);
      toast.success('Estimate saved');
      await fetchEstimate();
    } catch { toast.error('Failed to save estimate'); }
    finally { setSaving(false); }
  };

  const searchCustomers = async (q) => {
    try {
      const res = await customersAPI.list({ search: q, page_size: 20 });
      return (res.data.customers || []).map(c => ({ value: c.id, label: c.display_name }));
    } catch { return []; }
  };

  const handleAssign = async (employeeIds) => {
    try {
      await estimatesAPI.assign(id, { employee_ids: employeeIds });
      toast.success('Assignments updated');
      await fetchEstimate();
    } catch { toast.error('Failed to update assignments'); }
  };

  const toggleAssignment = (userId) => {
    const current = estimate.assignments.map(a => a.employee_id);
    const next = current.includes(userId) ? current.filter(i => i !== userId) : [...current, userId];
    handleAssign(next);
  };

  // Items

  // Normalises empty-string form values to null before sending to the
  // backend. Pydantic refuses to parse "" as a number, so any field
  // left blank (e.g. commission rate when a sale has no commission)
  // would otherwise produce a 422. UUID-ish fields are also normalised
  // because the DB FK can't accept an empty string either.
  const sanitizeItemPayload = (raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    const NULLABLE_FIELDS = [
      'description', 'image_url', 'option_id', 'price_book_item_id',
      'sold_by_user_id', 'commission_recipient_user_id',
      'commission_rate_percent', 'cost', 'quantity', 'unit_price',
      'discount_percent', 'sort_order',
    ];
    const out = { ...raw };
    for (const f of NULLABLE_FIELDS) {
      if (out[f] === '' || out[f] === undefined) out[f] = null;
    }
    return out;
  };

  const openAddItem = (type, optionId = null) => {
    setNewItem({ type, name: '', description: '', quantity: 1, unit_price: 0, cost: 0, option_id: optionId, taxable: true, discount_percent: 0, sold_by_user_id: '', commissionable: false, commission_recipient_user_id: '', commission_rate_percent: '' });
    setEditingItemId(null);
  };

  const saveNewItem = async () => {
    if (!newItem?.name.trim()) { toast.error('Name is required'); return; }
    try {
      await estimatesAPI.addItem(id, sanitizeItemPayload(newItem));
      toast.success('Item added');
      setNewItem(null);
      await fetchEstimate();
    } catch { toast.error('Failed to add item'); }
  };

  const openEditItem = (item) => {
    setEditingItemId(item.id);
    setItemForm({ type: item.type, name: item.name, description: item.description || '', quantity: item.quantity, unit_price: item.unit_price, cost: item.cost || 0, image_url: item.image_url || '', taxable: item.taxable !== false, discount_percent: item.discount_percent || 0, sold_by_user_id: item.sold_by_user_id || '', commissionable: item.commissionable || false, commission_recipient_user_id: item.commission_recipient_user_id || '', commission_rate_percent: item.commission_rate_percent ?? '' });
    setNewItem(null);
  };

  const saveEditItem = async () => {
    if (!itemForm.name?.trim()) { toast.error('Name is required'); return; }
    try {
      await estimatesAPI.updateItem(editingItemId, sanitizeItemPayload(itemForm));
      toast.success('Item updated');
      setEditingItemId(null);
      await fetchEstimate();
    } catch { toast.error('Failed to update item'); }
  };

  const deleteItem = async (itemId) => {
    try {
      await estimatesAPI.removeItem(itemId);
      toast.success('Item removed');
      await fetchEstimate();
    } catch { toast.error('Failed to remove item'); }
  };

  // Notes
  const addNote = async () => {
    if (!noteText.trim()) return;
    setNoteSaving(true);
    try {
      await estimatesAPI.addNote(id, { note: noteText.trim() });
      setNoteText('');
      toast.success('Note added');
      await fetchEstimate();
    } catch { toast.error('Failed to add note'); }
    finally { setNoteSaving(false); }
  };

  // Options
  const createOption = async (name) => {
    const n = name || optionName.trim();
    if (!n) { toast.error('Option name is required'); return; }
    setCreatingOption(true);
    try {
      await estimatesAPI.createOption(id, { name: n });
      toast.success(`Option "${n}" created`);
      setOptionName('');
      await fetchEstimate();
    } catch { toast.error('Failed to create option'); }
    finally { setCreatingOption(false); }
  };

  const createGoodBetterBest = async () => {
    setCreatingOption(true);
    try {
      await estimatesAPI.createOption(id, { name: 'Good', sort_order: 0 });
      await estimatesAPI.createOption(id, { name: 'Better', sort_order: 1, is_recommended: true });
      await estimatesAPI.createOption(id, { name: 'Best', sort_order: 2 });
      toast.success('Good / Better / Best options created');
      await fetchEstimate();
    } catch { toast.error('Failed to create options'); }
    finally { setCreatingOption(false); }
  };

  const deleteOption = async (optionId) => {
    try {
      await estimatesAPI.removeOption(optionId);
      toast.success('Option deleted');
      await fetchEstimate();
    } catch { toast.error('Failed to delete option'); }
  };

  const selectOption = async (optionId) => {
    try {
      await estimatesAPI.selectOption(optionId);
      toast.success('Option selected');
      await fetchEstimate();
    } catch { toast.error('Failed to select option'); }
  };

  const updateOptionField = async (optionId, field, value) => {
    try {
      await estimatesAPI.updateOption(optionId, { [field]: value });
      await fetchEstimate();
    } catch { toast.error('Failed to update option'); }
  };

  const toggleOptionExpand = (optId) => {
    setExpandedOptions(prev => ({ ...prev, [optId]: !prev[optId] }));
  };

  // Convert to Job
  const convertToJob = async () => {
    try {
      const res = await estimatesAPI.convertToJob(id);
      toast.success(`Converted to Job ${res.data.job.job_number}`);
      await fetchEstimate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to convert');
    }
  };

  // Approval Link
  const generateLink = async () => {
    try {
      const res = await estimatesAPI.generateLink(id);
      await fetchEstimate();
      const url = `${window.location.origin}/approve/${res.data.approval_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Approval link copied to clipboard');
    } catch { toast.error('Failed to generate link'); }
  };

  const revokeLink = async () => {
    try {
      await estimatesAPI.revokeLink(id);
      toast.success('Approval link revoked');
      await fetchEstimate();
    } catch { toast.error('Failed to revoke link'); }
  };

  const copyLink = async () => {
    if (!estimate.approval_token) return;
    const url = `${window.location.origin}/approve/${estimate.approval_token}`;
    await navigator.clipboard.writeText(url);
    toast.success('Link copied');
  };

  // Text Estimate
  const [textingSms, setTextingSms] = useState(false);
  const handleTextEstimate = async () => {
    setTextingSms(true);
    try {
      const res = await estimatesAPI.sendText(id);
      toast.success(`Estimate texted to ${res.data.sent_to}`);
      await fetchEstimate();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to text estimate';
      toast.error(detail);
    } finally {
      setTextingSms(false);
    }
  };

  // Send approval email
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [attachPdf, setAttachPdf] = useState(false);

  const handleSendEmail = async () => {
    if (!sendEmail.trim() || !sendEmail.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setSending(true);
    try {
      await estimatesAPI.sendApproval(id, { method: 'email', to: sendEmail.trim(), attach_pdf: attachPdf });
      toast.success(`Approval link sent to ${sendEmail}`);
      setShowSend(false);
      setSendEmail('');
      setAttachPdf(false);
      await fetchEstimate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (loading || isNew) {
    return <Layout><div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /><span className="ml-2 text-sm text-muted-foreground">{isNew ? 'Creating estimate...' : 'Loading...'}</span></div></Layout>;
  }
  if (!estimate) return null;

  const baseServices = estimate.items.filter(i => i.type === 'service');
  const baseMaterials = estimate.items.filter(i => i.type === 'material');
  const options = estimate.options || [];
  const hasOptions = options.length > 0;
  const isConverted = estimate.status === 'copied_to_job' || estimate.status === 'converted_to_job';

  return (
    <Layout>
      <div className="space-y-4" data-testid="estimate-editor-page">
        {/* Sticky Context Bar */}
        <div className="sticky top-14 z-30 -mx-4 px-4 md:-mx-6 md:px-6 pb-3 pt-1 bg-background/95 backdrop-blur-sm border-b border-border/50 shadow-[0_2px_8px_rgba(0,0,0,0.06)]" data-testid="estimate-sticky-bar">
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/estimates')} data-testid="back-to-estimates">
              <ArrowLeft className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Estimates</span>
            </Button>
            <span className="text-muted-foreground/40">|</span>
            <h1 className="text-lg font-bold tracking-tight truncate" data-testid="estimate-number">{estimate.estimate_number}</h1>
            <StatusBadge status={estimate.status} />
            {customer && <span className="hidden md:inline text-sm text-muted-foreground truncate max-w-[200px]">{customer.display_name}</span>}
            {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            {canEdit && !isConverted && (
              <Button
                variant={hasUnsavedChanges ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs gap-1.5 ml-auto"
                onClick={saveEstimate}
                disabled={saving || !hasUnsavedChanges}
                data-testid="save-estimate-btn"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? 'Saving...' : hasUnsavedChanges ? 'Save Estimate' : 'Saved'}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && !isConverted && (
              <Select value={estimate.status} onValueChange={(v) => updateField('status', v)}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.filter(s => s.value !== 'copied_to_job').map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {/* Desktop: show all action buttons */}
            <div className="hidden sm:flex items-center gap-2">
              {canEdit && !isConverted && (
                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={convertToJob} data-testid="convert-to-job-btn">
                    <ArrowRight className="w-3.5 h-3.5 mr-1" /> Convert to Job
                  </Button>
                </TooltipTrigger><TooltipContent>Create a job from this estimate</TooltipContent></Tooltip></TooltipProvider>
              )}
              {canEdit && !isConverted && (
                <TooltipProvider><Tooltip><TooltipTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs" onClick={async () => {
                    try {
                      const res = await invoicesAPI.fromEstimate(id);
                      toast.success(`Invoice ${res.data.invoice_number} created from estimate`);
                      navigate(`/invoices/${res.data.id}`);
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create invoice'); }
                  }} data-testid="create-invoice-from-estimate-btn">
                    <Receipt className="w-3.5 h-3.5 mr-1" /> Create Invoice
                  </Button>
                </TooltipTrigger><TooltipContent>Create an invoice from this estimate's line items</TooltipContent></Tooltip></TooltipProvider>
              )}
              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={async () => {
                  try {
                    const res = await estimatesAPI.downloadPdf(id);
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                    const a = document.createElement('a'); a.href = url; a.download = `${estimate.estimate_number}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                  } catch { toast.error('Failed to generate PDF'); }
                }} data-testid="download-pdf-btn">
                  <Download className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
              </TooltipTrigger><TooltipContent>Download estimate as PDF</TooltipContent></Tooltip></TooltipProvider>
              {isConverted && estimate.converted_job_id && (
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate(`/jobs/${estimate.converted_job_id}`)} data-testid="view-job-btn">
                  <Briefcase className="w-3.5 h-3.5 mr-1" /> View Job
                </Button>
              )}
            </div>
            {/* Mobile: kebab overflow menu */}
            <div className="sm:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="h-8 w-8" data-testid="estimate-mobile-actions">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {canEdit && !isConverted && (
                    <DropdownMenuItem onClick={convertToJob} data-testid="mobile-convert-to-job">
                      <ArrowRight className="w-3.5 h-3.5 mr-2" /> Convert to Job
                    </DropdownMenuItem>
                  )}
                  {canEdit && !isConverted && (
                    <DropdownMenuItem onClick={async () => {
                      try {
                        const res = await invoicesAPI.fromEstimate(id);
                        toast.success(`Invoice ${res.data.invoice_number} created`);
                        navigate(`/invoices/${res.data.id}`);
                      } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
                    }} data-testid="mobile-create-invoice">
                      <Receipt className="w-3.5 h-3.5 mr-2" /> Create Invoice
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={async () => {
                    try {
                      const res = await estimatesAPI.downloadPdf(id);
                      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                      const a = document.createElement('a'); a.href = url; a.download = `${estimate.estimate_number}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                    } catch { toast.error('Failed to generate PDF'); }
                  }} data-testid="mobile-download-pdf">
                    <Download className="w-3.5 h-3.5 mr-2" /> Download PDF
                  </DropdownMenuItem>
                  {isConverted && estimate.converted_job_id && (
                    <DropdownMenuItem onClick={() => navigate(`/jobs/${estimate.converted_job_id}`)} data-testid="mobile-view-job">
                      <Briefcase className="w-3.5 h-3.5 mr-2" /> View Job
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Two-column layout — stacks on mobile */}
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
          {/* LEFT SIDEBAR */}
          <div className="space-y-4">
            {/* Customer Card */}
            <Card data-testid="customer-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Customer</CardTitle>
              </CardHeader>
              <CardContent>
                {customer ? (
                  <div className="space-y-1.5">
                    <p className="font-semibold text-sm cursor-pointer hover:text-primary" onClick={() => navigate(`/customers/${customer.id}`)} data-testid="customer-link">{customer.display_name}</p>
                    {customer.primary_phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" /> {customer.primary_phone}</p>}
                    {customer.billing_email && <p className="text-xs text-muted-foreground flex items-center gap-1">@ {customer.billing_email}</p>}
                    {estimate.customer_location ? (
                      <LocationBadge location={estimate.customer_location} className="text-xs" />
                    ) : customer.service_street && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" /> {customer.service_street}, {customer.service_city}</p>
                    )}
                    <LocationPicker
                      locations={customer.locations}
                      value={estimate.customer_location_id}
                      onChange={(locId) => updateField('customer_location_id', locId || '')}
                      disabled={!canEdit}
                      className="mt-2"
                    />
                    <BillingBadge billingCustomer={estimate.billing_customer} className="mt-2" />
                  </div>
                ) : canEdit ? (
                  <AsyncSearchableSelect value={estimate.customer_id || ''} onValueChange={(v) => updateField('customer_id', v)} searchFn={searchCustomers} placeholder="Type name, phone, or email..." focusHint="Type name, phone, or email to find a customer..." data-testid="assign-customer" />
                ) : (
                  <p className="text-sm text-muted-foreground italic">No customer assigned</p>
                )}
                {customer && canEdit && (
                  <Button variant="ghost" size="sm" className="w-full mt-2 text-xs" onClick={() => { updateField('customer_id', ''); setCustomer(null); }}>Change Customer</Button>
                )}
              </CardContent>
            </Card>

            {/* Schedule */}
            <Card data-testid="schedule-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Clock className="w-3 h-3" /> Schedule</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label className="text-xs">Start</Label>
                  <Input type="datetime-local" value={estimate.scheduled_start ? estimate.scheduled_start.slice(0, 16) : ''} onChange={(e) => updateField('scheduled_start', e.target.value || '')} disabled={!canEdit} className="text-xs" data-testid="input-scheduled-start" />
                </div>
                <div>
                  <Label className="text-xs">End</Label>
                  <Input type="datetime-local" value={estimate.scheduled_end ? estimate.scheduled_end.slice(0, 16) : ''} onChange={(e) => updateField('scheduled_end', e.target.value || '')} disabled={!canEdit} className="text-xs" data-testid="input-scheduled-end" />
                </div>
                {estimate.estimate_appointment_reminder_sent_at && (
                  <p className="text-[11px] text-green-600 dark:text-green-400 font-medium mt-1" data-testid="estimate-reminder-sent-badge">
                    Reminder sent {new Date(estimate.estimate_appointment_reminder_sent_at).toLocaleDateString()}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Assigned Employees */}
            <Card data-testid="assignments-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><User className="w-3 h-3" /> Assigned Employees</CardTitle>
              </CardHeader>
              <CardContent>
                <EmployeeMultiSelect
                  users={users}
                  assigned={estimate.assignments.map(a => a.employee_id)}
                  onToggle={(userId) => canEdit && toggleAssignment(userId)}
                  disabled={!canEdit}
                />
              </CardContent>
            </Card>

            {/* Tags, Lead Source, Name & Description */}
            <Card data-testid="metadata-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Tag className="w-3 h-3" /> Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <Label className="text-xs">Estimate Name</Label>
                  <Input value={localName} onChange={(e) => { setLocalName(e.target.value); setNameDirty(true); }} onBlur={() => { if (nameDirty) saveEstimate(); }} disabled={!canEdit} placeholder="e.g. Kitchen Remodel" className="text-xs" data-testid="input-estimate-name" />
                </div>
                <div>
                  <Label className="text-xs">Tags</Label>
                  <TagChipInput
                    value={estimate.tags || ''}
                    onChange={(v) => updateField('tags', v)}
                    suggestions={tagSuggestions}
                    disabled={!canEdit}
                    data-testid="input-tags"
                  />
                </div>
                <div>
                  <Label className="text-xs">Lead Source</Label>
                  <LeadSourcePicker
                    value={estimate.lead_source || ''}
                    onChange={(v) => updateField('lead_source', v)}
                    leadSources={leadSources}
                    onCreateSource={async (name) => {
                      try {
                        const res = await leadSourcesAPI.create({ name });
                        setLeadSources(prev => [...prev, res.data]);
                        return res.data.name;
                      } catch { return null; }
                    }}
                    disabled={!canEdit}
                    data-testid="input-lead-source"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Deposit Settings */}
            {canEdit && !isConverted && (
              <Card data-testid="deposit-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><CreditCard className="w-3 h-3" /> Deposit Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="text-xs">Deposit Type</Label>
                    <Select value={estimate.deposit_type || 'none'} onValueChange={(v) => updateField('deposit_type', v === 'none' ? '' : v)}>
                      <SelectTrigger className="h-7 text-xs" data-testid="deposit-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Deposit</SelectItem>
                        <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                        <SelectItem value="percentage">Percentage (%)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {estimate.deposit_type && estimate.deposit_type !== 'none' && (
                    <div>
                      <Label className="text-xs">{estimate.deposit_type === 'fixed' ? 'Amount ($)' : 'Percentage (%)'}</Label>
                      <Input
                        type="number"
                        step={estimate.deposit_type === 'fixed' ? '0.01' : '1'}
                        min="0"
                        value={estimate.deposit_amount ?? ''}
                        onChange={(e) => updateField('deposit_amount', parseFloat(e.target.value) || 0)}
                        placeholder={estimate.deposit_type === 'fixed' ? '500.00' : '25'}
                        className="text-xs h-7"
                        data-testid="deposit-amount-input"
                      />
                      {estimate.deposit_type === 'percentage' && estimate.deposit_amount > 0 && (
                        <p className="text-[10px] text-muted-foreground mt-0.5">
                          = ${(estimate.total * estimate.deposit_amount / 100).toFixed(2)} deposit
                        </p>
                      )}
                    </div>
                  )}
                  {estimate.deposit_collected && (
                    <div className="flex items-center gap-1 text-green-600 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Deposit collected
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Upsells moved below grid for full-width layout */}

            {/* Approval Link Card */}
            <Card data-testid="approval-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1"><Shield className="w-3 h-3" /> Customer Approval</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {estimate.approved_at ? (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-green-600">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span className="text-xs font-medium">Approved</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      By <span className="font-medium">{estimate.approval_name}</span> on {new Date(estimate.approved_at).toLocaleDateString()}
                    </p>
                    {/* Show approved option details */}
                    {estimate.approved_option_id && (() => {
                      const approvedOpt = options.find(o => o.id === estimate.approved_option_id);
                      return approvedOpt ? (
                        <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-md p-2 mt-1" data-testid="approved-option-info">
                          <p className="text-[10px] text-muted-foreground">Approved Option</p>
                          <p className="text-xs font-semibold">{approvedOpt.name}</p>
                          <p className="text-xs font-bold text-green-700 dark:text-green-400">${approvedOpt.total.toFixed(2)}</p>
                        </div>
                      ) : null;
                    })()}
                    {estimate.signature_url && (
                      <div data-testid="estimate-signature">
                        <p className="text-[10px] text-muted-foreground mb-1">Signature</p>
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${estimate.signature_url}`} alt="Customer signature" className="border rounded bg-white max-w-full max-h-20" />
                      </div>
                    )}
                    {estimate.approval_sent_at && (
                      <p className="text-[10px] text-muted-foreground">
                        Sent via {estimate.approval_sent_via} to {estimate.approval_sent_to}
                      </p>
                    )}
                    {estimate.view_count > 0 && (
                      <p className="text-[10px] text-muted-foreground" data-testid="estimate-view-tracking">
                        Opened {estimate.view_count} time{estimate.view_count !== 1 ? 's' : ''}
                        {estimate.last_viewed_at && <> &middot; last {new Date(estimate.last_viewed_at).toLocaleDateString()}</>}
                      </p>
                    )}
                    {estimate.estimate_appointment_reminder_sent_at && (
                      <p className="text-[10px] text-green-600 dark:text-green-400" data-testid="estimate-reminder-sent-badge">
                        Reminder sent {new Date(estimate.estimate_appointment_reminder_sent_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ) : estimate.approval_token ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Link2 className="w-3.5 h-3.5 text-blue-500" />
                      <span className="text-xs font-medium text-blue-600">Link active</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm" className="text-xs flex-1 h-7" onClick={copyLink} data-testid="copy-approval-link">
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                      <TooltipProvider><Tooltip><TooltipTrigger asChild>
                        <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => window.open(`/approve/${estimate.approval_token}`, '_blank')} data-testid="preview-approval-link">
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger><TooltipContent>Preview approval page</TooltipContent></Tooltip></TooltipProvider>
                    </div>
                    {/* Send Estimate */}
                    <Button variant="default" size="sm" className="text-xs w-full h-8" onClick={() => {
                      setShowSend(true);
                      if (estimate.billing_customer?.billing_email) setSendEmail(estimate.billing_customer.billing_email);
                      else if (customer?.billing_email) setSendEmail(customer.billing_email);
                    }} data-testid="open-send-estimate">
                      <Send className="w-3.5 h-3.5 mr-1" /> Send Estimate
                    </Button>
                    {/* Text Estimate */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs w-full h-8"
                      onClick={handleTextEstimate}
                      disabled={textingSms}
                      data-testid="text-estimate-button"
                    >
                      {textingSms ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Phone className="w-3.5 h-3.5 mr-1" />}
                      Text Estimate
                    </Button>
                    {/* Delivery status */}
                    {estimate.approval_sent_at && (
                      <p className="text-[10px] text-muted-foreground pt-1 border-t">
                        Last sent via {estimate.approval_sent_via} to {estimate.approval_sent_to} — {new Date(estimate.approval_sent_at).toLocaleDateString()}
                      </p>
                    )}
                    <Button variant="ghost" size="sm" className="text-xs h-6 text-destructive/60 hover:text-destructive w-full" onClick={revokeLink} data-testid="revoke-approval-link">
                      <XCircle className="w-3 h-3 mr-1" /> Revoke Link
                    </Button>
                    {estimate.view_count > 0 && (
                      <p className="text-[10px] text-muted-foreground pt-1 border-t" data-testid="estimate-view-tracking">
                        Opened {estimate.view_count} time{estimate.view_count !== 1 ? 's' : ''}
                        {estimate.last_viewed_at && <> &middot; last {new Date(estimate.last_viewed_at).toLocaleDateString()}</>}
                      </p>
                    )}
                  </div>
                ) : canEdit ? (
                  <Button variant="outline" size="sm" className="text-xs w-full" onClick={generateLink} data-testid="generate-approval-link">
                    <Link2 className="w-3 h-3 mr-1" /> Generate Approval Link
                  </Button>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No approval link</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* MAIN PANEL */}
          <div className="space-y-4">
            {/* ═══ Unified Estimate Builder Card ═══ */}
            <Card data-testid="estimate-builder-card">
              <CardContent className="py-5 space-y-0">
                {/* ── Services Section ── */}
                <div data-testid="services-section">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold flex items-center gap-2" data-testid="services-header">
                      <Briefcase className="w-4 h-4 text-muted-foreground" /> Services ({baseServices.length})
                      {hasOptions && <span className="text-[10px] text-muted-foreground font-normal ml-1">— always included</span>}
                    </h3>
                    {canEdit && !isConverted && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" data-testid="add-service-dropdown">
                            <Plus className="w-3 h-3" /> Add <ChevronDown className="w-3 h-3 opacity-50" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => { setPbPickerOptionId(null); setPbPickerOpen(true); }} data-testid="add-from-pricebook-btn">
                            <BookOpen className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> From Price Book
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setTemplatePickerOpen(true)} data-testid="apply-template-btn">
                            <Layers className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> From Template
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openAddItem('service')} data-testid="add-service-btn">
                            <Plus className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> Manual Entry
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  <ItemsTable items={baseServices} editingId={editingItemId} itemForm={itemForm} setItemForm={setItemForm} onEdit={openEditItem} onSave={saveEditItem} onCancel={() => setEditingItemId(null)} onDelete={deleteItem} canEdit={canEdit && !isConverted} users={users} />
                  {newItem?.type === 'service' && !newItem?.option_id && (
                    <NewItemRow item={newItem} setItem={setNewItem} onSave={saveNewItem} onCancel={() => setNewItem(null)} users={users} />
                  )}
                </div>

                {/* Divider */}
                <div className="border-t my-5" />

                {/* ── Materials Section ── */}
                <div data-testid="materials-section">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold flex items-center gap-2" data-testid="materials-header">
                      <FileText className="w-4 h-4 text-muted-foreground" /> Materials ({baseMaterials.length})
                      {hasOptions && <span className="text-[10px] text-muted-foreground font-normal ml-1">— always included</span>}
                    </h3>
                    {canEdit && !isConverted && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5" data-testid="add-material-dropdown">
                            <Plus className="w-3 h-3" /> Add <ChevronDown className="w-3 h-3 opacity-50" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => { setPbPickerOptionId(null); setPbPickerOpen(true); }} data-testid="add-material-from-pricebook-btn">
                            <BookOpen className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> From Price Book
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openAddItem('material')} data-testid="add-material-btn">
                            <Plus className="w-3.5 h-3.5 mr-2 text-muted-foreground" /> Manual Entry
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  <ItemsTable items={baseMaterials} editingId={editingItemId} itemForm={itemForm} setItemForm={setItemForm} onEdit={openEditItem} onSave={saveEditItem} onCancel={() => setEditingItemId(null)} onDelete={deleteItem} canEdit={canEdit && !isConverted} users={users} />
                  {newItem?.type === 'material' && !newItem?.option_id && (
                    <NewItemRow item={newItem} setItem={setNewItem} onSave={saveNewItem} onCancel={() => setNewItem(null)} itemType="material" users={users} />
                  )}
                </div>

                {/* Divider */}
                <div className="border-t my-5" />

                {/* ── Options Section ── */}
                <div data-testid="options-section">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Layers className="w-4 h-4 text-muted-foreground" /> Options ({options.length})
                    </h3>
                  </div>
                  {canEdit && !isConverted && (
                    <div className="flex items-center gap-2 mb-4 pt-1">
                      <Input
                        value={optionName}
                        onChange={(e) => setOptionName(e.target.value)}
                        placeholder="Option name (e.g. Premium Package)..."
                        className="h-8 text-xs max-w-xs"
                        onKeyDown={(e) => e.key === 'Enter' && createOption()}
                        data-testid="input-option-name"
                      />
                      <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5 shrink-0" onClick={() => createOption()} disabled={creatingOption || !optionName.trim()} data-testid="add-option-btn">
                        {creatingOption ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add Option
                      </Button>
                    </div>
                  )}
                  <div className="space-y-4">
                    {options.length === 0 ? (
                      <div className="text-center py-6 bg-muted/30 rounded-lg" data-testid="options-empty-state">
                        <Layers className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                        <p className="text-sm font-medium mb-1">No options yet</p>
                        <p className="text-xs text-muted-foreground mb-4">Give your customer multiple choices to increase close rates.</p>
                        {canEdit && !isConverted && (
                          <Button variant="default" size="sm" onClick={createGoodBetterBest} disabled={creatingOption} data-testid="add-gbb-btn">
                            {creatingOption ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Layers className="w-3.5 h-3.5 mr-1.5" />}
                            Add Good / Better / Best
                          </Button>
                        )}
                      </div>
                    ) : (
                      options.map(opt => (
                        <OptionCard
                          key={opt.id}
                          option={opt}
                          expanded={expandedOptions[opt.id]}
                          onToggleExpand={() => toggleOptionExpand(opt.id)}
                          onSelect={() => selectOption(opt.id)}
                          onDelete={() => deleteOption(opt.id)}
                          onUpdateField={(field, val) => updateOptionField(opt.id, field, val)}
                          onAddItem={(type) => openAddItem(type, opt.id)}
                          onAddFromPriceBook={() => { setPbPickerOptionId(opt.id); setPbPickerOpen(true); }}
                          newItem={newItem}
                          setNewItem={setNewItem}
                          onSaveNewItem={saveNewItem}
                          editingItemId={editingItemId}
                          itemForm={itemForm}
                          setItemForm={setItemForm}
                          onEditItem={openEditItem}
                          onSaveEditItem={saveEditItem}
                          onCancelEdit={() => setEditingItemId(null)}
                          onDeleteItem={deleteItem}
                          canEdit={canEdit && !isConverted}
                          onRefresh={fetchEstimate}
                          users={users}
                        />
                      ))
                    )}
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t my-5" />

                {/* ── Totals Section ── */}
                <div data-testid="totals-section">
                  <div className="flex justify-end">
                    <div className="w-80 space-y-2">
                      {hasOptions && (
                        <div className="text-xs text-muted-foreground mb-2 text-right">
                          {options.some(o => o.is_selected)
                            ? `Includes base items + "${options.find(o => o.is_selected)?.name}"`
                            : 'No option selected — showing base items only'}
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Subtotal</span>
                        <span className="font-medium" data-testid="subtotal">${estimate.subtotal.toFixed(2)}</span>
                      </div>
                      {/* Document-level discount */}
                      {canEdit && !isConverted ? (
                        <div className="flex justify-between text-sm items-center gap-2" data-testid="estimate-discount-controls">
                          <span className="text-muted-foreground">Discount</span>
                          <div className="flex items-center gap-2">
                            <Select value={estimate.discount_type || 'none'} onValueChange={(v) => updateField('discount_type', v === 'none' ? null : v)}>
                              <SelectTrigger className="w-24 h-7 text-xs" data-testid="estimate-discount-type"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">None</SelectItem>
                                <SelectItem value="percentage">%</SelectItem>
                                <SelectItem value="fixed">$ Fixed</SelectItem>
                              </SelectContent>
                            </Select>
                            {estimate.discount_type && (
                              <>
                                <Input
                                  type="number" step="0.01" min="0"
                                  value={estimate.discount_value || ''}
                                  onChange={(e) => updateField('discount_value', parseFloat(e.target.value) || 0)}
                                  className="w-16 text-right text-xs h-7"
                                  data-testid="estimate-discount-value"
                                />
                                <span className="text-xs text-muted-foreground">{estimate.discount_type === 'percentage' ? '%' : '$'}</span>
                                <span className="text-xs font-medium text-red-600" data-testid="estimate-discount-total">-${(estimate.discount_total || 0).toFixed(2)}</span>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (estimate.discount_total > 0 && (
                        <div className="flex justify-between text-sm text-red-600">
                          <span>Discount {estimate.discount_type === 'percentage' ? `(${estimate.discount_value}%)` : ''}</span>
                          <span className="font-medium" data-testid="estimate-discount-total">-${estimate.discount_total.toFixed(2)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm items-center gap-2">
                        <span className="text-muted-foreground">Tax</span>
                        <div className="flex items-center gap-2">
                          {canEdit && !isConverted ? (
                            <>
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  step="0.001"
                                  min="0"
                                  value={estimate.tax_rate ?? ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? -1 : parseFloat(e.target.value);
                                    updateField('tax_rate', val);
                                  }}
                                  placeholder="Rate"
                                  className="w-20 text-right text-xs h-7"
                                  data-testid="input-tax-rate"
                                />
                                <span className="text-xs text-muted-foreground">%</span>
                              </div>
                              <span className="text-xs font-medium" data-testid="tax-amount">${estimate.tax_amount.toFixed(2)}</span>
                            </>
                          ) : (
                            <>
                              {estimate.tax_rate != null && <span className="text-xs text-muted-foreground">({estimate.tax_rate}%)</span>}
                              <span className="font-medium">${estimate.tax_amount.toFixed(2)}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-between text-base font-bold border-t pt-2">
                        <span>Total</span>
                        <span data-testid="total">${estimate.total.toFixed(2)}</span>
                      </div>
                      {/* Cost / Profit summary if cost data is available */}
                      {(() => {
                        const allItems = [...(estimate.items || [])];
                        const selectedOpt = options.find(o => o.is_selected);
                        if (selectedOpt) allItems.push(...(selectedOpt.items || []));
                        const totalCost = allItems.reduce((s, i) => s + (i.cost || 0) * i.quantity, 0);
                        if (totalCost > 0) {
                          const profit = estimate.subtotal - totalCost;
                          const margin = estimate.subtotal > 0 ? (profit / estimate.subtotal * 100) : 0;
                          return (
                            <div className="border-t pt-2 mt-1 space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Total Cost</span>
                                <span>${totalCost.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Profit</span>
                                <span className={profit >= 0 ? 'text-green-600' : 'text-red-600'}>${profit.toFixed(2)}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="text-muted-foreground">Margin</span>
                                <span className={margin >= 0 ? 'text-green-600' : 'text-red-600'}>{margin.toFixed(1)}%</span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ═══ Private Notes ═══ */}
            <Card data-testid="notes-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Private Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {canEdit && (
                  <div className="flex gap-2">
                    <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add an internal note..." rows={2} className="text-sm" data-testid="input-note" />
                    <Button size="sm" onClick={addNote} disabled={noteSaving || !noteText.trim()} className="shrink-0 self-end" data-testid="add-note-btn">
                      {noteSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
                {estimate.notes.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {estimate.notes.map(n => (
                      <div key={n.id} className="text-sm border-l-2 border-muted pl-3 py-1" data-testid={`note-${n.id}`}>
                        <p className="whitespace-pre-wrap">{n.note}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{n.author_name || 'Unknown'} — {n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground italic">No notes yet.</p>
                )}
              </CardContent>
            </Card>

            {/* ═══ Maintenance Plan (V1: max 1 per estimate) ═══ */}
            {(() => {
              const mpUpsell = (estimate.upsells || []).find(u => u.maintenance_plan_id);
              const linkedPlan = mpUpsell ? maintenancePlans.find(p => p.id === mpUpsell.maintenance_plan_id) : null;
              return (
                <Card className="p-0 border-blue-200 dark:border-blue-800" data-testid="maintenance-plan-card">
                  <CardHeader className="px-6 pb-3 pt-5">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm font-medium flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-600" /> Maintenance Plan
                        {mpUpsell && <Badge className="text-xs bg-blue-600 text-white hover:bg-blue-700">Included</Badge>}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="px-6 pb-5 space-y-3">
                    {mpUpsell ? (
                      <div className="flex items-center justify-between py-3 px-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-950/20" data-testid="maintenance-plan-attached">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">{mpUpsell.name}</span>
                            <Badge className="text-xs bg-blue-600 text-white hover:bg-blue-700 gap-1">
                              <RefreshCw className="w-3 h-3" /> {linkedPlan?.billing_frequency || 'recurring'}
                            </Badge>
                          </div>
                          {mpUpsell.description && <p className="text-xs text-muted-foreground mt-0.5">{mpUpsell.description}</p>}
                          {linkedPlan && <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">{linkedPlan.visits_per_year} visit{linkedPlan.visits_per_year !== 1 ? 's' : ''}/year</p>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-base font-bold">${mpUpsell.price.toFixed(2)}</span>
                          {canEdit && !isConverted && (
                            <Button variant="ghost" size="sm" className="h-8 text-xs text-destructive hover:text-destructive hover:bg-red-50" onClick={async () => {
                              try {
                                await estimatesAPI.removeUpsell(id, mpUpsell.id);
                                fetchEstimate();
                                toast.success('Maintenance plan removed');
                              } catch { toast.error('Failed to remove plan'); }
                            }} data-testid="remove-maintenance-plan-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Remove</Button>
                          )}
                        </div>
                      </div>
                    ) : canEdit && !isConverted ? (
                      maintenancePlans.length > 0 ? (
                        <div className="space-y-2" data-testid="maintenance-plan-picker">
                          <select
                            className="w-full text-sm h-9 rounded-md border px-3 text-foreground bg-background"
                            defaultValue=""
                            data-testid="maintenance-plan-select"
                            onChange={async (e) => {
                              const planId = e.target.value;
                              if (!planId) return;
                              const plan = maintenancePlans.find(p => p.id === planId);
                              if (!plan) return;
                              try {
                                await estimatesAPI.createUpsell(id, {
                                  name: plan.name,
                                  price: plan.price,
                                  description: plan.description || `${plan.visits_per_year} visit${plan.visits_per_year !== 1 ? 's' : ''}/year — billed ${plan.billing_frequency}`,
                                  maintenance_plan_id: plan.id,
                                });
                                fetchEstimate();
                                toast.success('Maintenance plan added to estimate');
                              } catch (err) {
                                toast.error(err?.response?.data?.detail || 'Failed to add plan');
                              }
                              e.target.value = '';
                            }}
                          >
                            <option value="">Select a maintenance plan...</option>
                            {maintenancePlans.map(p => (
                              <option key={p.id} value={p.id}>{p.name} — ${p.price.toFixed(2)}/{p.billing_frequency}</option>
                            ))}
                          </select>
                          <p className="text-xs text-muted-foreground">When this estimate is approved, the customer will be automatically enrolled in the selected plan.</p>
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground italic py-2">No maintenance plans configured. <a href="/maintenance-plans" className="text-blue-600 underline">Create a plan</a> first.</p>
                      )
                    ) : (
                      <p className="text-xs text-muted-foreground italic py-2">No maintenance plan on this estimate.</p>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* ═══ Upsells / Add-ons (inside main column) ═══ */}
            <Card className="p-0" data-testid="upsells-card">
              <CardHeader className="px-6 pb-3 pt-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Tag className="w-4 h-4" /> Upsells / Add-ons
                    {(estimate.upsells || []).filter(u => !u.maintenance_plan_id).length > 0 && (
                      <span className="text-xs text-muted-foreground font-normal">({(estimate.upsells || []).filter(u => !u.maintenance_plan_id).length})</span>
                    )}
                  </CardTitle>
                  {canEdit && !isConverted && (
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => {
                      setNewUpsell({ name: '', description: '', price: '', maintenance_plan_id: '' });
                    }} data-testid="add-upsell-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="px-6 pb-5 space-y-3">
                {(estimate.upsells || []).filter(u => !u.maintenance_plan_id).length === 0 && !newUpsell && (
                  <p className="text-sm text-muted-foreground italic py-2">No upsells configured. Add optional add-ons that customers can select during approval.</p>
                )}
                {(estimate.upsells || []).filter(u => !u.maintenance_plan_id).map(u => (
                  <div key={u.id} className="flex items-center justify-between py-3 px-4 rounded-lg border bg-card hover:shadow-sm transition-shadow group" data-testid={`upsell-row-${u.id}`}>
                    {editingUpsellId === u.id ? (
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2 items-center">
                          <Input value={upsellForm.name || ''} onChange={e => setUpsellForm(f => ({ ...f, name: e.target.value }))} className="text-sm h-8 flex-1" placeholder="Name" />
                          <Input type="number" step="0.01" value={upsellForm.price || ''} onChange={e => setUpsellForm(f => ({ ...f, price: e.target.value }))} className="text-sm h-8 w-24" placeholder="Price" />
                          <Button variant="default" size="sm" className="h-8" onClick={async () => {
                            try {
                              await estimatesAPI.updateUpsell(id, u.id, { name: upsellForm.name, price: parseFloat(upsellForm.price) || 0, description: upsellForm.description || '', maintenance_plan_id: upsellForm.maintenance_plan_id || '' });
                              fetchEstimate();
                              setEditingUpsellId(null);
                            } catch { toast.error('Failed to update'); }
                          }}><Save className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="h-8" onClick={() => setEditingUpsellId(null)}><X className="w-3.5 h-3.5" /></Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">{u.name}</span>
                            {(estimate.approved_upsell_ids || []).includes(u.id) && (
                              <Badge className="text-xs bg-emerald-600 text-white hover:bg-emerald-700 gap-1">
                                <Check className="w-3 h-3" /> Selected
                              </Badge>
                            )}
                          </div>
                          {u.description && <p className="text-xs text-muted-foreground mt-0.5">{u.description}</p>}
                          {u.value_note && <p className="text-xs text-blue-600 dark:text-blue-400 italic mt-0.5">{u.value_note}</p>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-base font-bold">${u.price.toFixed(2)}</span>
                          {canEdit && !isConverted && (
                            <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition-opacity">
                              <button onClick={() => { setEditingUpsellId(u.id); setUpsellForm({ name: u.name, description: u.description || '', price: u.price, maintenance_plan_id: u.maintenance_plan_id || '' }); }} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
                              <button onClick={async () => { await estimatesAPI.removeUpsell(id, u.id); fetchEstimate(); }} className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {newUpsell && (
                  <div className="flex flex-col gap-2 p-4 border rounded-lg bg-muted/30" data-testid="new-upsell-form">
                    <div className="flex gap-2 items-center">
                      <Input value={newUpsell.name} onChange={e => setNewUpsell(f => ({ ...f, name: e.target.value }))} className="text-sm h-8 flex-1" placeholder="Name (e.g. Extended Warranty)" data-testid="upsell-name-input" />
                      <Input type="number" step="0.01" value={newUpsell.price} onChange={e => setNewUpsell(f => ({ ...f, price: e.target.value }))} className="text-sm h-8 w-24" placeholder="Price" data-testid="upsell-price-input" />
                      <Button variant="default" size="sm" className="h-8" onClick={async () => {
                        if (!newUpsell.name.trim()) return;
                        try {
                          await estimatesAPI.createUpsell(id, { name: newUpsell.name, price: parseFloat(newUpsell.price) || 0, description: newUpsell.description || '', value_note: newUpsell.value_note || '', maintenance_plan_id: newUpsell.maintenance_plan_id || null });
                          fetchEstimate();
                          setNewUpsell(null);
                        } catch { toast.error('Failed to add upsell'); }
                      }} data-testid="save-upsell-btn"><Save className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="h-8" onClick={() => setNewUpsell(null)}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                    <Input value={newUpsell.value_note || ''} onChange={e => setNewUpsell(f => ({ ...f, value_note: e.target.value }))} className="text-sm h-8" placeholder="Value note (e.g. Save $120/year vs one-time visits)" data-testid="upsell-value-note-input" />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <PriceBookPicker
        open={pbPickerOpen}
        onOpenChange={setPbPickerOpen}
        targetType="estimate"
        targetId={id}
        optionId={pbPickerOptionId}
        onAdd={() => fetchEstimate()}
      />
      <ServiceTemplatePicker
        open={templatePickerOpen}
        onOpenChange={setTemplatePickerOpen}
        estimateId={id}
        optionId={null}
        onApply={() => fetchEstimate()}
      />

      {/* Send Estimate Modal */}
      <Dialog open={showSend} onOpenChange={(v) => { if (!v) { setShowSend(false); setSendEmail(''); setAttachPdf(false); } }}>
        <DialogContent className="sm:max-w-md" data-testid="send-estimate-modal">
          <DialogHeader>
            <DialogTitle className="text-base">Send Estimate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs mb-1.5 block">Recipient Email</Label>
              <Input value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder="customer@email.com" className="text-sm" data-testid="send-email-input" />
            </div>
            <div>
              <Label className="text-xs mb-1.5 block text-muted-foreground">Recipient Phone</Label>
              <div className="relative">
                <Input value={customer?.phone || customer?.secondary_phone || ''} readOnly className="text-sm bg-muted/30 pr-24" data-testid="send-phone-input" />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">SMS coming soon</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="send-email-check" checked disabled className="data-[state=checked]:bg-primary" />
                <label htmlFor="send-email-check" className="text-sm flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> Send Email</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="send-sms-check" disabled />
                <label htmlFor="send-sms-check" className="text-sm flex items-center gap-1.5 text-muted-foreground"><Phone className="w-3.5 h-3.5" /> Send SMS <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded">Coming soon</span></label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="attach-pdf-check" checked={attachPdf} onCheckedChange={setAttachPdf} data-testid="attach-pdf-checkbox" />
              <label htmlFor="attach-pdf-check" className="text-sm flex items-center gap-1.5 cursor-pointer"><FileText className="w-3.5 h-3.5" /> Attach PDF</label>
            </div>
            <Button onClick={async () => { await handleSendEmail(); setShowSend(false); }} disabled={sending || !sendEmail.trim()} className="w-full h-10 gap-2" data-testid="send-estimate-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send Estimate
            </Button>
            {estimate.approval_sent_at && (
              <p className="text-[11px] text-muted-foreground text-center">
                Last sent via {estimate.approval_sent_via} to {estimate.approval_sent_to} — {new Date(estimate.approval_sent_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

/* ---- Option Card ---- */
function OptionCard({ option, expanded, onToggleExpand, onSelect, onDelete, onUpdateField, onAddItem, onAddFromPriceBook, newItem, setNewItem, onSaveNewItem, editingItemId, itemForm, setItemForm, onEditItem, onSaveEditItem, onCancelEdit, onDeleteItem, canEdit, onRefresh, users }) {
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(option.name);
  const [descVal, setDescVal] = useState(option.description || '');
  const [recVal, setRecVal] = useState(option.recommendation_reason || '');
  const [mediaUploading, setMediaUploading] = useState(false);

  const optServices = option.items.filter(i => i.type === 'service');
  const optMaterials = option.items.filter(i => i.type === 'material');
  const optMedia = option.media || [];

  // Color accents per package tier
  const tierColors = {
    'Good': { border: 'border-l-emerald-500', headerBg: 'bg-emerald-50/60', selectedHeaderBg: 'bg-emerald-100/80', accent: 'text-emerald-700', selectBtn: 'border-emerald-400 text-emerald-700 hover:bg-emerald-50', dot: 'bg-emerald-500', selectedRing: 'ring-emerald-400/60', selectedBadge: 'bg-emerald-600 text-white' },
    'Better': { border: 'border-l-blue-500', headerBg: 'bg-blue-50/60', selectedHeaderBg: 'bg-blue-100/80', accent: 'text-blue-700', selectBtn: 'border-blue-400 text-blue-700 hover:bg-blue-50', dot: 'bg-blue-500', selectedRing: 'ring-blue-400/60', selectedBadge: 'bg-blue-600 text-white' },
    'Best': { border: 'border-l-violet-500', headerBg: 'bg-violet-50/60', selectedHeaderBg: 'bg-violet-100/80', accent: 'text-violet-700', selectBtn: 'border-violet-400 text-violet-700 hover:bg-violet-50', dot: 'bg-violet-500', selectedRing: 'ring-violet-400/60', selectedBadge: 'bg-violet-600 text-white' },
  };
  const tier = tierColors[option.name] || { border: 'border-l-gray-400', headerBg: 'bg-muted/40', selectedHeaderBg: 'bg-muted/60', accent: 'text-muted-foreground', selectBtn: 'border-muted-foreground/40 text-muted-foreground hover:bg-muted/30', dot: 'bg-muted-foreground', selectedRing: 'ring-primary/40', selectedBadge: 'bg-primary text-primary-foreground' };

  const saveName = () => {
    if (nameVal.trim() && nameVal !== option.name) onUpdateField('name', nameVal.trim());
    setEditingName(false);
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMediaUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` },
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      await estimatesAPI.addOptionMedia(option.id, { media_type: data.media_type, file_url: data.url });
      toast.success(`${data.media_type === 'video' ? 'Video' : 'Image'} uploaded`);
      if (onRefresh) onRefresh();
    } catch { toast.error('Failed to upload media'); }
    finally { setMediaUploading(false); e.target.value = ''; }
  };

  const handleRemoveMedia = async (mediaId) => {
    try {
      await estimatesAPI.removeMedia(mediaId);
      toast.success('Media removed');
      if (onRefresh) onRefresh();
    } catch { toast.error('Failed to remove media'); }
  };

  return (
    <div
      className={`border rounded-lg border-l-4 ${tier.border} transition-all overflow-hidden ${option.is_selected ? `ring-2 ${tier.selectedRing} shadow-md` : 'bg-card'}`}
      data-testid={`option-card-${option.id}`}
    >
      {/* Option Header — tinted background for block separation */}
      <div className={`flex items-center gap-3 px-4 py-3 ${option.is_selected ? tier.selectedHeaderBg : tier.headerBg}`}>
        <button onClick={onToggleExpand} className="shrink-0 text-muted-foreground hover:text-foreground" data-testid={`toggle-option-${option.id}`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <div className={`w-2 h-2 rounded-full shrink-0 ${tier.dot}`} />
        <div className="flex-1 min-w-0">
          {editingName ? (
            <div className="flex items-center gap-1">
              <Input value={nameVal} onChange={(e) => setNameVal(e.target.value)} className="h-7 text-sm font-bold" onBlur={saveName} onKeyDown={(e) => e.key === 'Enter' && saveName()} autoFocus />
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold truncate cursor-pointer hover:opacity-70 ${tier.accent}`} onClick={() => canEdit && setEditingName(true)} data-testid={`option-name-${option.id}`}>
                {option.name}
              </span>
              {option.is_recommended && <Badge className="bg-amber-100 text-amber-800 text-[9px] gap-0.5 border border-amber-200"><Star className="w-2.5 h-2.5 fill-amber-600" /> Most Popular</Badge>}
              {option.is_selected && <Badge className={`text-[9px] shadow-sm ${tier.selectedBadge}`}><Check className="w-2.5 h-2.5 mr-0.5" /> Selected</Badge>}
            </div>
          )}
          <div className="text-[10px] text-muted-foreground mt-0.5">
            {option.items.length} item{option.items.length !== 1 ? 's' : ''} · ${option.subtotal.toFixed(2)}
            {option.tax_amount > 0 && ` + $${option.tax_amount.toFixed(2)} tax`}
            {' '}= <span className="font-semibold text-foreground">${option.total.toFixed(2)}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {canEdit && (
            <TooltipProvider><Tooltip><TooltipTrigger asChild>
              <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${option.is_recommended ? 'text-amber-500' : 'text-muted-foreground/40 hover:text-amber-500'}`}
                onClick={() => onUpdateField('is_recommended', !option.is_recommended)} data-testid={`recommend-option-${option.id}`}>
                <Star className={`w-3.5 h-3.5 ${option.is_recommended ? 'fill-amber-500' : ''}`} />
              </Button>
            </TooltipTrigger><TooltipContent>{option.is_recommended ? 'Remove recommendation' : 'Mark as recommended'}</TooltipContent></Tooltip></TooltipProvider>
          )}
          {canEdit && !option.is_selected && (
            <Button variant="outline" size="sm" className={`h-7 text-xs px-3 font-medium ${tier.selectBtn}`} onClick={onSelect} data-testid={`select-option-${option.id}`}>
              Select
            </Button>
          )}
          {canEdit && option.is_selected && (
            <div className={`h-7 text-xs px-3 font-semibold rounded-md flex items-center gap-1 shadow-sm ${tier.selectedBadge}`}>
              <Check className="w-3.5 h-3.5" /> Selected
            </div>
          )}
          {canEdit && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive/40 hover:text-destructive" onClick={onDelete} data-testid={`delete-option-${option.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Description */}
          <div className="pt-3">
            {canEdit ? (
              <Textarea
                value={descVal}
                onChange={(e) => setDescVal(e.target.value)}
                onBlur={() => { if (descVal !== (option.description || '')) onUpdateField('description', descVal); }}
                placeholder="Describe this option for the customer..."
                rows={2}
                className="text-xs resize-none"
                data-testid={`option-desc-${option.id}`}
              />
            ) : option.description ? (
              <p className="text-xs text-muted-foreground">{option.description}</p>
            ) : null}
          </div>

          {/* Recommendation Reason — emphasized with amber accent */}
          {option.is_recommended && (
            <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3" data-testid={`rec-reason-section-${option.id}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Star className="w-3.5 h-3.5 text-amber-600 fill-amber-500" />
                <span className="text-[11px] font-semibold text-amber-800 uppercase tracking-wider">Recommendation Reason</span>
              </div>
              {canEdit ? (
                <Textarea
                  value={recVal}
                  onChange={(e) => setRecVal(e.target.value)}
                  onBlur={() => { if (recVal !== (option.recommendation_reason || '')) onUpdateField('recommendation_reason', recVal); }}
                  placeholder="Why do you recommend this option? (shown to customer)"
                  rows={2}
                  className="text-xs resize-none bg-white/80"
                  data-testid={`option-rec-reason-${option.id}`}
                />
              ) : option.recommendation_reason ? (
                <p className="text-xs text-amber-800">{option.recommendation_reason}</p>
              ) : null}
            </div>
          )}

          {/* ── Evidence / Media ── */}
          <div className="rounded-md border border-dashed border-border bg-muted/30 p-3" data-testid={`evidence-section-${option.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <ImageIcon className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Evidence ({optMedia.length})</span>
              {canEdit && (
                <label className="inline-flex items-center gap-1.5 text-xs font-medium text-primary cursor-pointer hover:text-primary/80 ml-1 bg-primary/10 rounded-md px-2 py-1 transition-colors" data-testid={`upload-media-${option.id}`}>
                  {mediaUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {mediaUploading ? 'Uploading...' : 'Add Photo / Video'}
                  <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="hidden" onChange={handleMediaUpload} disabled={mediaUploading} />
                </label>
              )}
            </div>
            {optMedia.length > 0 ? (
              <div className="flex gap-2 flex-wrap">
                {optMedia.map((m) => (
                  <div key={m.id} className="relative group" data-testid={`media-item-${m.id}`}>
                    {m.media_type === 'video' ? (
                      <video src={m.file_url.startsWith('http') ? m.file_url : `${process.env.REACT_APP_BACKEND_URL}${m.file_url}`} className="w-24 h-18 object-cover rounded border bg-black" preload="metadata" />
                    ) : (
                      <img src={m.file_url.startsWith('http') ? m.file_url : `${process.env.REACT_APP_BACKEND_URL}${m.file_url}`} alt="" className="w-24 h-18 object-cover rounded border" />
                    )}
                    {m.media_type === 'video' && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <Video className="w-5 h-5 text-white drop-shadow" />
                      </div>
                    )}
                    {canEdit && (
                      <button onClick={() => handleRemoveMedia(m.id)} className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-media-${m.id}`}>
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              !canEdit ? null : (
                <p className="text-[11px] text-muted-foreground/60 italic">No photos or videos yet. Upload evidence to strengthen this option.</p>
              )
            )}
          </div>

          {/* ── Services ── */}
          <div className="rounded-md border border-border/60 bg-muted/20 p-3" data-testid={`services-section-${option.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Services ({optServices.length})</span>
              {canEdit && (
                <div className="flex gap-1 ml-1">
                  <Button variant="outline" size="sm" className="h-6 text-[11px] px-2 gap-1 bg-white shadow-sm" onClick={onAddFromPriceBook} data-testid={`opt-pricebook-${option.id}`}><BookOpen className="w-3 h-3" /> Price Book</Button>
                  <Button variant="ghost" size="sm" className="h-6 text-[11px] px-2 gap-1 text-muted-foreground" onClick={() => onAddItem('service')} data-testid={`add-opt-service-${option.id}`}><Plus className="w-3 h-3" /> Manual</Button>
                </div>
              )}
            </div>
            <ItemsTable items={optServices} editingId={editingItemId} itemForm={itemForm} setItemForm={setItemForm} onEdit={onEditItem} onSave={onSaveEditItem} onCancel={onCancelEdit} onDelete={onDeleteItem} canEdit={canEdit} users={users} />
            {newItem?.type === 'service' && newItem?.option_id === option.id && (
              <NewItemRow item={newItem} setItem={setNewItem} onSave={onSaveNewItem} onCancel={() => setNewItem(null)} users={users} />
            )}
          </div>

          {/* ── Materials ── */}
          <div className="rounded-md border border-border/60 bg-muted/20 p-3" data-testid={`materials-section-${option.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Materials ({optMaterials.length})</span>
              {canEdit && (
                <div className="flex gap-1 ml-1">
                  <Button variant="outline" size="sm" className="h-6 text-[11px] px-2 gap-1 bg-white shadow-sm" onClick={onAddFromPriceBook} data-testid={`opt-material-pricebook-${option.id}`}><BookOpen className="w-3 h-3" /> Price Book</Button>
                  <Button variant="ghost" size="sm" className="h-6 text-[11px] px-2 gap-1 text-muted-foreground" onClick={() => onAddItem('material')} data-testid={`add-opt-material-${option.id}`}><Plus className="w-3 h-3" /> Manual</Button>
                </div>
              )}
            </div>
            <ItemsTable items={optMaterials} editingId={editingItemId} itemForm={itemForm} setItemForm={setItemForm} onEdit={onEditItem} onSave={onSaveEditItem} onCancel={onCancelEdit} onDelete={onDeleteItem} canEdit={canEdit} users={users} />
            {newItem?.type === 'material' && newItem?.option_id === option.id && (
              <NewItemRow item={newItem} setItem={setNewItem} onSave={onSaveNewItem} onCancel={() => setNewItem(null)} itemType="material" users={users} />
            )}
          </div>

          {/* Option tax + total */}
          <div className="flex justify-end pt-3 border-t">
            <div className="w-52 space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Subtotal</span>
                <span>${option.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xs items-center">
                <span className="text-muted-foreground">Tax</span>
                {canEdit ? (
                  <Input type="number" step="0.01" value={option.tax_amount} onChange={(e) => onUpdateField('tax_amount', parseFloat(e.target.value) || 0)} className="w-20 text-right text-xs h-6" data-testid={`option-tax-${option.id}`} />
                ) : (
                  <span>${option.tax_amount.toFixed(2)}</span>
                )}
              </div>
              <div className="flex justify-between text-xs font-bold border-t pt-1">
                <span>Total</span>
                <span data-testid={`option-total-${option.id}`}>${option.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Commission Fields (reusable across edit & new item) ---- */
function CommissionFields({ form, setForm, users }) {
  const eligibleUsers = (users || []).filter(u => u.is_active);
  const handleRecipientChange = (userId) => {
    const user = eligibleUsers.find(u => u.id === userId);
    const newForm = { ...form, commission_recipient_user_id: userId || '' };
    if (user && user.commissions_enabled && user.default_commission_rate != null) {
      newForm.commission_rate_percent = user.default_commission_rate;
    }
    setForm(newForm);
  };
  return (
    <div className="pl-[48px] space-y-2 py-2 border-t border-dashed border-border/40" data-testid="commission-fields">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 min-w-[180px]">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider whitespace-nowrap">Sold by</span>
          <select className="h-7 text-xs rounded-md border border-input bg-background px-2 py-1 flex-1 min-w-[120px]"
            value={form.sold_by_user_id || ''} onChange={(e) => setForm({ ...form, sold_by_user_id: e.target.value || '' })}
            data-testid="sold-by-select">
            <option value="">None</option>
            {eligibleUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input type="checkbox" checked={form.commissionable || false}
            onChange={(e) => {
              const checked = e.target.checked;
              setForm({ ...form, commissionable: checked, ...(!checked ? { commission_recipient_user_id: '', commission_rate_percent: '' } : {}) });
            }} className="rounded border-muted-foreground/30" data-testid="commissionable-checkbox" />
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Commissionable</span>
        </label>
      </div>
      {form.commissionable && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5 min-w-[180px]">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider whitespace-nowrap">Recipient</span>
            <select className="h-7 text-xs rounded-md border border-input bg-background px-2 py-1 flex-1 min-w-[120px]"
              value={form.commission_recipient_user_id || ''} onChange={(e) => handleRecipientChange(e.target.value)}
              data-testid="commission-recipient-select">
              <option value="">Select...</option>
              {eligibleUsers.map(u => <option key={u.id} value={u.id}>{u.name}{u.commissions_enabled ? ` (${u.default_commission_rate || 0}%)` : ''}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider whitespace-nowrap">Rate</span>
            <div className="relative w-20">
              <input type="number" step="0.5" min="0" max="100"
                className="h-7 text-xs rounded-md border border-input bg-background px-2 py-1 pr-6 w-full text-right"
                value={form.commission_rate_percent ?? ''} onChange={(e) => setForm({ ...form, commission_rate_percent: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                placeholder="0" data-testid="commission-rate-input" />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-[10px]">%</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Items Table ---- */
function ItemsTable({ items, editingId, itemForm, setItemForm, onEdit, onSave, onCancel, onDelete, canEdit, users }) {
  if (items.length === 0) return <p className="text-xs text-muted-foreground italic py-1">No items.</p>;

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[40px_1fr_60px_80px_80px_64px] gap-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-2 py-1">
        <div />
        <div>Item</div>
        <div>Qty</div>
        <div className="text-right">Unit Price</div>
        <div className="text-right">Total</div>
        <div />
      </div>
      {items.map(item => (
        editingId === item.id ? (
          <div key={item.id} className="border rounded-lg p-3 bg-muted/50 space-y-2" data-testid={`editing-item-${item.id}`}>
            <div className="grid grid-cols-[40px_1fr_60px_80px_80px_64px] gap-2 items-center">
              <div className="w-10 h-10 rounded border bg-muted flex items-center justify-center overflow-hidden">
                {itemForm.image_url ? <img src={itemForm.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-muted-foreground/40" />}
              </div>
              <Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} className="h-7 text-xs" placeholder="Item name" />
              <Input type="number" value={itemForm.quantity} onChange={(e) => setItemForm({ ...itemForm, quantity: parseFloat(e.target.value) || 0 })} className="h-7 text-xs" />
              <Input type="number" step="0.01" value={itemForm.unit_price} onChange={(e) => setItemForm({ ...itemForm, unit_price: parseFloat(e.target.value) || 0 })} className="h-7 text-xs text-right" />
              <div className="text-right text-xs font-medium">${((itemForm.quantity * itemForm.unit_price) * (1 - (itemForm.discount_percent || 0) / 100)).toFixed(2)}</div>
              <div className="flex gap-1 justify-end">
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onSave} data-testid={`save-item-${item.id}`}><Save className="w-3 h-3" /></Button>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onCancel}><X className="w-3 h-3" /></Button>
              </div>
            </div>
            <div className="flex gap-2 pl-[48px]">
              <Textarea value={itemForm.description || ''} onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })} placeholder="Description / notes for this line item..." rows={2} className="text-xs flex-1 resize-none" />
              <div className="flex flex-col gap-1.5 shrink-0">
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input type="checkbox" checked={itemForm.taxable !== false} onChange={(e) => setItemForm({ ...itemForm, taxable: e.target.checked })} className="rounded border-muted-foreground/30" />
                  <span className="text-xs text-muted-foreground">Taxable</span>
                </label>
                <div className="flex items-center gap-1">
                  <span className="text-[10px] text-muted-foreground">Disc%</span>
                  <Input type="number" step="0.1" min="0" max="100" value={itemForm.discount_percent || ''} onChange={(e) => setItemForm({ ...itemForm, discount_percent: parseFloat(e.target.value) || 0 })} placeholder="0" className="h-6 text-[10px] text-right w-14" data-testid="edit-item-discount" />
                </div>
              </div>
            </div>
            <CommissionFields form={itemForm} setForm={setItemForm} users={users} />
          </div>
        ) : (
          <div key={item.id} className="grid grid-cols-[40px_1fr_60px_80px_80px_64px] gap-2 items-center px-2 py-1.5 hover:bg-muted/30 rounded group" data-testid={`item-row-${item.id}`}>
            <div className="w-10 h-10 rounded border bg-muted flex items-center justify-center overflow-hidden shrink-0">
              {item.image_url ? <img src={item.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-muted-foreground/40" />}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-medium truncate">{item.name}</p>
                {item.taxable === false && <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0 text-muted-foreground">Non-tax</Badge>}
                {item.discount_percent > 0 && <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0 text-red-600 border-red-200 bg-red-50">{item.discount_percent}% off</Badge>}
              </div>
              {item.description && <p className="text-[10px] text-muted-foreground truncate">{item.description}</p>}
              {(item.sold_by_user_id || item.commissionable) && (
                <div className="flex items-center gap-1 mt-0.5">
                  {item.sold_by_user_id && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0 text-blue-600 border-blue-200 bg-blue-50">
                      Sold: {(users || []).find(u => u.id === item.sold_by_user_id)?.name || 'Unknown'}
                    </Badge>
                  )}
                  {item.commissionable && item.commission_rate_percent != null && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 shrink-0 text-emerald-600 border-emerald-200 bg-emerald-50">
                      {item.commission_rate_percent}% comm
                    </Badge>
                  )}
                </div>
              )}
            </div>
            <div className="text-xs">{item.quantity}</div>
            <div className="text-xs text-right">${item.unit_price.toFixed(2)}</div>
            <div className="text-xs text-right font-medium">${item.total_price.toFixed(2)}</div>
            <div className="flex gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
              {canEdit && (
                <>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onEdit(item)} data-testid={`edit-item-${item.id}`}><Edit2 className="w-3 h-3" /></Button>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onDelete(item.id)} data-testid={`delete-item-${item.id}`}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                </>
              )}
            </div>
          </div>
        )
      ))}
    </div>
  );
}

/* ---- New Item Row ---- */
function NewItemRow({ item, setItem, onSave, onCancel, itemType, users }) {
  const [pbResults, setPbResults] = useState([]);
  const [pbSearching, setPbSearching] = useState(false);
  const [showPbDropdown, setShowPbDropdown] = useState(false);
  const searchTimeout = React.useRef(null);
  const containerRef = React.useRef(null);

  const isMaterial = itemType === 'material';

  // Price Book search for material names
  const handleNameChange = (e) => {
    const val = e.target.value;
    setItem({ ...item, name: val });

    if (isMaterial && val.length >= 2) {
      clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(async () => {
        setPbSearching(true);
        try {
          const res = await priceBookAPI.listItems({ search: val, item_type: 'material', page_size: 8 });
          setPbResults(res.data.items || []);
          setShowPbDropdown(true);
        } catch { setPbResults([]); }
        finally { setPbSearching(false); }
      }, 300);
    } else {
      setPbResults([]);
      setShowPbDropdown(false);
    }
  };

  const selectPbItem = (pbItem) => {
    setItem({
      ...item,
      name: pbItem.name,
      unit_price: pbItem.unit_price || 0,
      description: pbItem.description || '',
    });
    setShowPbDropdown(false);
    setPbResults([]);
  };

  // Close dropdown on outside click
  React.useEffect(() => {
    if (!showPbDropdown) return;
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setShowPbDropdown(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPbDropdown]);

  return (
    <div className="border rounded-lg p-3 bg-primary/5 mt-2 space-y-2" data-testid="new-item-row" ref={containerRef}>
      <div className="grid grid-cols-[40px_1fr_60px_80px_80px_64px] gap-2 items-center">
        <div className="w-10 h-10 rounded border bg-muted flex items-center justify-center">
          <ImageIcon className="w-4 h-4 text-muted-foreground/40" />
        </div>
        <div className="relative">
          <Input
            value={item.name}
            onChange={handleNameChange}
            onFocus={() => { if (isMaterial && pbResults.length > 0) setShowPbDropdown(true); }}
            placeholder={isMaterial ? "Search Price Book or type name..." : "Item name"}
            className="h-7 text-xs"
            data-testid="new-item-name"
            autoComplete="off"
          />
          {isMaterial && pbSearching && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 animate-spin text-muted-foreground" />
          )}
          {/* Price Book search dropdown */}
          {isMaterial && showPbDropdown && pbResults.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border/80 rounded-lg shadow-lg shadow-black/8 ring-1 ring-black/[0.04] animate-in fade-in-0 zoom-in-95 max-h-48 overflow-y-auto" data-testid="pb-search-dropdown">
              <div className="px-2 py-1 border-b bg-muted/30">
                <span className="text-[10px] text-muted-foreground font-medium">Price Book results</span>
              </div>
              {pbResults.map(pb => (
                <button
                  key={pb.id}
                  type="button"
                  className="w-full flex items-center justify-between px-2.5 py-1.5 text-xs hover:bg-accent transition-colors"
                  onMouseDown={(e) => { e.preventDefault(); selectPbItem(pb); }}
                  data-testid={`pb-result-${pb.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <BookOpen className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="truncate font-medium">{pb.name}</span>
                  </div>
                  <span className="text-muted-foreground shrink-0 ml-2">${(pb.unit_price || 0).toFixed(2)}</span>
                </button>
              ))}
            </div>
          )}
          {isMaterial && showPbDropdown && item.name.length >= 2 && pbResults.length === 0 && !pbSearching && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border/80 rounded-lg shadow-lg p-2 text-xs text-muted-foreground text-center">
              No Price Book matches — enter manually
            </div>
          )}
        </div>
        <Input type="number" value={item.quantity} onChange={(e) => setItem({ ...item, quantity: parseFloat(e.target.value) || 0 })} className="h-7 text-xs" data-testid="new-item-qty" />
        <Input type="number" step="0.01" value={item.unit_price} onChange={(e) => setItem({ ...item, unit_price: parseFloat(e.target.value) || 0 })} placeholder="Price" className="h-7 text-xs text-right" data-testid="new-item-price" />
        <div className="text-right text-xs font-medium">${(item.quantity * item.unit_price).toFixed(2)}</div>
        <div className="flex gap-1 justify-end">
          <Button variant="default" size="sm" className="h-6 text-xs px-2" onClick={onSave} data-testid="save-new-item"><Save className="w-3 h-3" /></Button>
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={onCancel}><X className="w-3 h-3" /></Button>
        </div>
      </div>
      <div className="flex gap-2 pl-[48px]">
        <Textarea value={item.description || ''} onChange={(e) => setItem({ ...item, description: e.target.value })} placeholder="Description / notes..." rows={1} className="text-xs flex-1 resize-none" data-testid="new-item-description" />
        <label className="flex items-center gap-1.5 shrink-0 cursor-pointer select-none">
          <input type="checkbox" checked={item.taxable !== false} onChange={(e) => setItem({ ...item, taxable: e.target.checked })} className="rounded border-muted-foreground/30" />
          <span className="text-xs text-muted-foreground">Taxable</span>
        </label>
      </div>
      <CommissionFields form={item} setForm={setItem} users={users} />
    </div>
  );
}


/* ---- Employee Multi-Select Dropdown ---- */
function EmployeeMultiSelect({ users, assigned, onToggle, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = React.useRef(null);

  const selectedUsers = users.filter(u => assigned.includes(u.id));
  const filtered = search.trim()
    ? users.filter(u => u.name.toLowerCase().includes(search.toLowerCase()))
    : users;

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} className="relative" data-testid="employee-multiselect">
      {/* Trigger — matches AsyncSearchableSelect container styling */}
      <button
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className={`flex items-center w-full h-9 rounded-md border border-input bg-transparent text-sm shadow-sm transition-colors hover:bg-muted/50 overflow-hidden ${open ? 'ring-1 ring-ring' : ''} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
        data-testid="employee-multiselect-trigger"
      >
        <User className="h-4 w-4 text-muted-foreground ml-3 shrink-0" />
        <div className="flex-1 flex items-center gap-1 min-w-0 overflow-hidden px-2">
          {selectedUsers.length === 0 && (
            <span className="text-muted-foreground text-sm">Select employees...</span>
          )}
          {selectedUsers.length >= 1 && selectedUsers.length <= 2 && (
            selectedUsers.map(u => (
              <span key={u.id} className="inline-flex items-center gap-1 bg-primary/10 text-primary rounded px-1.5 py-0.5 text-[11px] font-medium max-w-[120px] truncate" data-testid={`employee-chip-${u.id}`}>
                {u.name}
              </span>
            ))
          )}
          {selectedUsers.length >= 3 && (
            <span className="text-foreground text-sm font-medium">{selectedUsers.length} selected</span>
          )}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground shrink-0 mr-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-popover border border-border/80 rounded-lg shadow-lg shadow-black/8 ring-1 ring-black/[0.04] animate-in fade-in-0 zoom-in-95">
          {/* Search */}
          <div className="flex items-center gap-2 px-2.5 py-1.5 border-b">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employees..."
              className="w-full text-sm bg-transparent outline-none placeholder:text-muted-foreground"
              autoFocus
              data-testid="employee-search-input"
            />
          </div>
          <div className="max-h-48 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground italic text-center">
                {users.length === 0 ? 'No team members' : 'No matches'}
              </div>
            ) : (
              filtered.map(u => {
                const isAssigned = assigned.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => onToggle(u.id)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-sm transition-colors hover:bg-accent"
                    data-testid={`assign-user-${u.id}`}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${isAssigned ? 'bg-primary border-primary' : 'border-input'}`}>
                      {isAssigned && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <span className={`truncate ${isAssigned ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{u.name}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---- Lead Source Picker ---- */
function LeadSourcePicker({ value, onChange, leadSources, onCreateSource, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const filtered = search.trim()
    ? leadSources.filter(ls => ls.name.toLowerCase().includes(search.toLowerCase()))
    : leadSources.slice(0, 10); // Top 10 by usage (already sorted from backend)
  const exactMatch = leadSources.some(ls => ls.name.toLowerCase() === search.toLowerCase());

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) { setOpen(false); setSearch(''); } };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const displayValue = open ? search : (value || '');

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative" data-testid="lead-source-picker">
      {/* Input container — matches AsyncSearchableSelect */}
      <div
        className={`flex h-9 w-full items-center rounded-md border border-input bg-transparent text-sm shadow-sm transition-colors overflow-hidden ${open ? 'ring-1 ring-ring' : ''} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        <Search className="h-4 w-4 text-muted-foreground ml-3 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onFocus={() => { if (!disabled) { setOpen(true); setSearch(value || ''); } }}
          onChange={(e) => { setSearch(e.target.value); if (!open) setOpen(true); }}
          disabled={disabled}
          placeholder="Search lead sources..."
          className="flex-1 h-full px-2 py-2 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed text-sm"
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          data-testid="lead-source-input"
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="px-2 h-full flex items-center justify-center hover:bg-muted/50 transition-colors"
            tabIndex={-1}
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-popover border border-border/80 rounded-lg shadow-lg shadow-black/8 ring-1 ring-black/[0.04] animate-in fade-in-0 zoom-in-95">
          <ul className="max-h-[280px] overflow-y-auto p-1">
            {filtered.length === 0 && !search.trim() && (
              <li className="py-3 px-2 text-center text-sm text-muted-foreground">No lead sources configured</li>
            )}
            {filtered.length === 0 && search.trim() && !exactMatch && (
              <li className="py-3 px-2 text-center text-sm text-muted-foreground">No matches</li>
            )}
            {filtered.map(ls => (
              <li
                key={ls.id}
                className={`flex cursor-pointer select-none items-center justify-between rounded-sm px-2 py-2 text-sm transition-colors hover:bg-accent ${value === ls.name ? 'font-medium' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(ls.name);
                  setSearch('');
                  setOpen(false);
                }}
                data-testid={`lead-source-option-${ls.id}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Check className={`h-4 w-4 shrink-0 ${value === ls.name ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{ls.name}</span>
                </div>
                {ls.usage_count > 0 && <span className="text-xs text-muted-foreground/50 tabular-nums shrink-0 ml-2">{ls.usage_count}</span>}
              </li>
            ))}
            {search.trim() && !exactMatch && (
              <li
                className="flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm text-primary transition-colors hover:bg-accent border-t mt-1 pt-2"
                onMouseDown={async (e) => {
                  e.preventDefault();
                  const name = await onCreateSource(search.trim());
                  if (name) { onChange(name); }
                  setSearch('');
                  setOpen(false);
                }}
                data-testid="lead-source-create"
              >
                <Plus className="mr-2 h-4 w-4 shrink-0" />
                Create "{search.trim()}"
              </li>
            )}
          </ul>
          {filtered.length > 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground border-t bg-muted/30 rounded-b-lg">
              {search.trim() ? `${filtered.length} result${filtered.length !== 1 ? 's' : ''}` : 'Top sources by usage'} 
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---- Tag Chip Input ---- */
function TagChipInput({ value, onChange, suggestions, disabled }) {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = React.useRef(null);

  const tags = value ? value.split(',').map(t => t.trim()).filter(Boolean) : [];
  const uniqueSuggestions = [...new Set([...suggestions])].filter(s => !tags.includes(s));
  const filtered = input.trim()
    ? uniqueSuggestions.filter(s => s.toLowerCase().includes(input.toLowerCase()))
    : uniqueSuggestions;

  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (!trimmed || tags.includes(trimmed)) return;
    const next = [...tags, trimmed].join(', ');
    onChange(next);
    setInput('');
  };

  const removeTag = (idx) => {
    const next = tags.filter((_, i) => i !== idx).join(', ');
    onChange(next || '');
  };

  return (
    <div className="relative" data-testid="tag-chip-input">
      <div className="flex flex-wrap gap-1 min-h-[28px] p-1 border rounded-md bg-background">
        {tags.map((tag, idx) => (
          <Badge key={idx} variant="secondary" className="text-[10px] gap-1 px-1.5 py-0.5">
            {tag}
            {!disabled && (
              <button onClick={() => removeTag(idx)} className="hover:text-destructive" data-testid={`remove-tag-${idx}`}>
                <X className="w-2.5 h-2.5" />
              </button>
            )}
          </Badge>
        ))}
        {!disabled && (
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 200)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                if (input.trim()) addTag(input);
              }
              if (e.key === 'Backspace' && !input && tags.length > 0) {
                removeTag(tags.length - 1);
              }
            }}
            placeholder={tags.length === 0 ? 'Add tags...' : ''}
            className="flex-1 min-w-[60px] text-xs bg-transparent outline-none placeholder:text-muted-foreground"
            data-testid="tag-input-field"
          />
        )}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-32 overflow-auto">
          {filtered.slice(0, 10).map(s => (
            <button
              key={s}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                addTag(s);
              }}
              data-testid={`tag-suggestion-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
